<template>
  <div>
    <div
      v-if="!allDataLoaded"
      class="loading-overlay">
      <div class="loader">
        <span>Indlæsning af data&hellip;</span>
      </div>
    </div>
    <transition name="fade">

      <component
        :is="layout"
        class="page-wrapper"
        :is-load-error="loadError">
        <router-view
          :key="$route.fullPath"
          :is-load-error="loadError"
          @loadStateFromFile="handleLodFromFile" />
      </component>
    </transition>
    <cookie-banner ref="cookie-banner" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import { checkAutosave, clearAutosave } from '@/utils/AutoSave.js';
import CookieBanner from '@/components/popups/CookieBanner.vue';
import StateUtils from '@/utils/StateUtils.js';

export default {
  components: {
    'cookie-banner': CookieBanner
  },
  data () {
    return {
      autosaveData: null,
      initialDataLoaded: false,
      isFetched: false,
      loadError: false,
      priceLoadError: false,
      isLoadingSavedSession: false
    }
  },
  computed: {
    ...mapGetters('creator', [
      'creatorMode',
      'doors',
      'doorsNumberObject'
    ]),
    allDataLoaded () {
      return this.initialDataLoaded && this.isFetched;
    },
    layout () {
      if (!this.$route.meta) {
        return 'default-layout';
      }

      return `${this.$route.meta.layout || 'default'}-layout`;
    },
    shouldRestoreSession () {
      return this.isLoadingSavedSession && this.allDataLoaded;
    },
    creatorLastStep() {
      const creatorSteps = StateUtils.getStepsForCreatorMode(this.creatorMode);
      const lastStepInternalName = creatorSteps[creatorSteps.length - 1];
      const routeMap = StateUtils.ROUTE_NAME_MAP;
      return routeMap[lastStepInternalName] || lastStepInternalName;
    }
  },
  watch: {
    '$route': function (to, from) {
      const lastStepRouterName = this.creatorLastStep;

      if (from && from.name === lastStepRouterName && to.name === 'orderSummary') {
        this.syncCreatorToCart();
      }
    },
    shouldRestoreSession (newValue) {
      if (!newValue) {
        return;
      }
      this.restoreSession();
    }
  },
  mounted () {
    FormUtils.loadData(this, {
      method: 'get',
      endpoint: '/api/init',
      loadedKey: 'initialDataLoaded',
      errorKey: 'loadError',
      noPagination: true,
      successAction: (response) => {
        this.$store.commit('apiData/setApiData', response);
        this.isFetched = true;
      },
      failAction: () => {
        this.initialDataLoaded = true;
      }
    });

    this.checkAndHandleSession();
  },
  methods: {
    ...mapActions('cart', ['initNewWardrobe', 'syncCreatorToCart']),
    async checkAndHandleSession () {
      const savedSession = checkAutosave();

      if (!savedSession || !savedSession.isValid) {
        this.initNewWardrobe();
        return;
      }

      this.autosaveData = savedSession.data;
      let dialogText = '';
      if (this.autosaveData) {
        dialogText = `Projektet blev gemt den ${savedSession.date}. Det indeholder ${savedSession.wardrobeCount} ${savedSession.wardrobeCount === 1 ? 'garderobeskab' : 'garderobeskabe'}. Vil du indlæse denne autogem?`;
      } else {
        dialogText = `Det gemte projekt indeholder ${savedSession.wardrobeCount} ${savedSession.wardrobeCount === 1 ? 'garderobeskab' : 'garderobeskabe'}. Vil du indlæse denne autogem?`;
      }

      const shouldLoadExisting = await Vue.swal({
        title: 'Automatisk gemt projekt fundet',
        text: dialogText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Indlæs',
        cancelButtonText: 'Opret nyt projekt',
        allowOutsideClick: false
      });

      this.isLoadingSavedSession = shouldLoadExisting.isConfirmed;

      if (!shouldLoadExisting.isConfirmed) {
        this.discardSession();
      }
    },
    restoreSession () {
      if (!this.autosaveData) {
        console.warn('No session to restore');
        this.discardSession();
        return;
      }

      this.$store.commit('cart/setCartState', this.autosaveData);

      if (this.$route.name !== 'orderSummary') {
        this.$router.push({ name: 'orderSummary' });
      }
    },
    discardSession () {
      clearAutosave();
      this.initNewWardrobe();
      this.autosaveData = null;
    },
    handleLodFromFile (stateInFile) {
      const savedSession = checkAutosave(stateInFile);

      if (!savedSession || !savedSession.isValid) {
        console.error('Invalid configuration');
        return;
      }

      this.autosaveData = savedSession.data;
      this.restoreSession();
    }
  }
}
</script>
